import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn.js";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-[background-color,color,border-color,box-shadow,transform] duration-150 ease-emphasized cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:translate-y-px",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-sm hover:bg-primary/92 hover:shadow-md",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/92",
        outline:
          "border border-border-strong bg-card text-foreground shadow-xs hover:bg-accent hover:text-accent-foreground hover:border-border-strong",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost:
          "text-foreground hover:bg-accent hover:text-accent-foreground",
        soft:
          "bg-primary-soft text-accent-foreground hover:bg-accent",
        link:
          "text-primary underline-offset-4 hover:underline shadow-none"
      },
      size: {
        default: "h-10 px-3.5 py-2 lg:h-9",
        sm: "h-9 rounded-md px-3 text-xs lg:h-8",
        lg: "h-10 rounded-md px-5 text-sm",
        xl: "h-11 rounded-lg px-6 text-sm",
        icon: "h-10 w-10 lg:h-9 lg:w-9",
        "icon-sm": "h-10 w-10 rounded-md lg:h-8 lg:w-8"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => (
    <button ref={ref} className={cn(buttonVariants({ variant, size, className }))} {...props} />
  )
);
Button.displayName = "Button";

export { buttonVariants };
